import React, { useState, useEffect } from 'react';
import { Bell, Clock, CheckCircle, AlertTriangle, AlertCircle, Check, ListTodo, Siren } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import useModalBackButton from '../hooks/useModalBackButton';
import { canOpenNotificationRoute } from '../utils/notificationNavigation';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import WindowModalHeader from './WindowModalHeader';

const NotificationModal = ({ isOpen, onClose, onMarkAllAsRead, onNotificationRead }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('unread');
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const { showToast } = useToast();
  const [allNotifications, setAllNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useModalBackButton(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, activeTab]);

  const fetchNotifications = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'unread') {
        const data = await safeFetch(`${API_URL}/api/notifications/unread`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setUnreadNotifications(data);
      } else {
        const data = await safeFetch(`${API_URL}/api/notifications`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setAllNotifications(data);
      }
    } catch (err) {
      console.error('Ошибка загрузки уведомлений:', err);
      setError('Ошибка подключения к серверу');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await safeFetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Обновляем список уведомлений
      if (activeTab === 'unread') {
        setUnreadNotifications(prev => 
          prev.filter(notification => notification.id !== notificationId)
        );
      } else {
        setAllNotifications(prev => 
          prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, is_read: true } 
              : notification
          )
        );
      }
      
      // Уведомляем Header об изменении
      if (onNotificationRead) {
        onNotificationRead();
      }
      window.dispatchEvent(new CustomEvent('notifications:changed', {
        detail: { delta: -1 }
      }));
    } catch (err) {
      console.error('Ошибка при отметке уведомления как прочитанного:', err);
    }
  };

  const openNotification = async (notification) => {
    if (!notification?.url) {
      if (!notification?.is_read) {
        await markAsRead(notification.id);
      }
      return;
    }

    const access = await canOpenNotificationRoute(notification.url);
    if (!access.allowed) {
      const msg = access.reason || 'Не удалось открыть уведомление';
      setError(msg);
      showToast(msg, 'error');
      return;
    }

    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    const route = (() => {
      if (!access.route?.startsWith('/order/')) return access.route;
      const [pathname, search = ''] = access.route.split('?');
      const params = new URLSearchParams(search);
      params.delete('from');
      params.delete('tab');
      params.delete('stage');
      params.set('notification', '1');
      return `${pathname}?${params.toString()}`;
    })();

    onClose();
    navigate(route);
  };

  const markAllAsRead = async () => {
    try {
      await safeFetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Обновляем списки уведомлений
      setUnreadNotifications([]);
      setAllNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
      
      onMarkAllAsRead();
      window.dispatchEvent(new CustomEvent('notifications:changed', {
        detail: { unreadCount: 0 }
      }));
    } catch (err) {
      console.error('Ошибка при отметке всех уведомлений как прочитанных:', err);
    }
  };

  const getNotificationIcon = (notification) => {
    const emergencyNotification = /экстренн(ый|ого|ому|ым|ом|ость|ости|остию)/i.test(
      `${notification?.title || ''} ${notification?.message || ''}`
    );
    if (emergencyNotification) {
      return <Siren className="h-5 w-5 text-fuchsia-600 dark:text-fuchsia-300" />;
    }

    const type = notification?.notification_type;
    switch (type) {
      case 'task':
        return <ListTodo className="w-5 h-5 text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const notifications = activeTab === 'unread' ? unreadNotifications : allNotifications;

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      variant="window"
      swipeToClose={false}
      containerClassName="select-none !items-start !pb-0 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-4"
      panelClassName="flex h-[min(72vh,640px)] min-h-0 max-h-[calc(100vh-1.5rem)] max-w-md flex-col select-none overflow-hidden"
      panelProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'notifications-modal-title',
        style: { padding: 0 },
      }}
    >
        <WindowModalHeader
          icon={Bell}
          title="Уведомления"
          titleId="notifications-modal-title"
          onClose={onClose}
          closeLabel="Закрыть уведомления"
        />
        
        {/* Tabs */}
        <div className="shrink-0 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <div className="flex rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setActiveTab('unread');
              }}
              className={`flex-1 rounded-lg px-4 py-2 text-[13px] font-medium transition-all ${
                activeTab === 'unread'
                  ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900'
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
              }`}
            >
              Непрочитанные
            </button>
            <button
              type="button"
              onClick={() => {
                haptic('selection');
                setActiveTab('all');
              }}
              className={`flex-1 rounded-lg px-4 py-2 text-[13px] font-medium transition-all ${
                activeTab === 'all'
                  ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900'
                  : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
              }`}
            >
              Все
            </button>
          </div>
        </div>
        
        {/* Notifications List */}
        <div className="flex-1 min-h-0 overflow-y-auto p-0 tg-allow-scroll">
          {loading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-3">
                  <div className="shimmer w-8 h-8 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="shimmer h-3.5 rounded w-3/4" />
                    <div className="shimmer h-3 rounded w-full" />
                    <div className="shimmer h-3 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex min-h-full items-center justify-center p-8 text-center">
              <div>
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-500 dark:text-red-400 mb-4">{error}</p>
              <button 
                onClick={fetchNotifications}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Повторить попытку
              </button>
              </div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex min-h-full items-center justify-center p-8 text-center">
              <div>
                <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {activeTab === 'unread' 
                    ? 'Нет непрочитанных уведомлений' 
                    : 'Нет уведомлений'}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {notifications.map((notification) => (
                <div 
                  key={notification.id} 
                  onClick={() => openNotification(notification)}
                  className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    !notification.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  } ${notification.url ? 'cursor-pointer' : ''}`}
                >
                  <div className="flex items-start space-x-2.5">
                    <div className="pt-0.5">
                      {getNotificationIcon(notification)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium leading-5 text-gray-900 dark:text-white truncate">
                          {notification.title}
                        </h4>
                        {!notification.is_read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[13px] leading-4.5 text-gray-600 dark:text-gray-400">
                        {notification.message}
                      </p>
                      <div className="mt-1.5 flex items-center text-[11px] text-gray-500 dark:text-gray-400">
                        <Clock className="mr-1 h-3 w-3" />
                        {formatDate(notification.created_at)}
                      </div>
                    </div>
                    {!notification.is_read && (
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          markAsRead(notification.id);
                        }}
                        className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
                        title="Отметить как прочитанное"
                      >
                        <Check className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {activeTab === 'unread' && unreadNotifications.length > 0 && (
          <div className="shrink-0 border-t border-gray-200 p-3 dark:border-gray-700">
            <button
              type="button"
              onClick={markAllAsRead}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-600 px-3 py-2.5 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-blue-900/20"
            >
              <Check className="h-4 w-4" />
              Прочитать все
            </button>
          </div>
        )}
    </ModalShell>
  );
};

export default NotificationModal;
